import { useEffect, useMemo } from "react";
import "./App.css";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-photo-view/dist/react-photo-view.css";
import axios from "axios";
import { Toaster } from "react-hot-toast";
import AlertDialog from "./components/alert/AlertDialog";
import { Box, Button, styled, createTheme, ThemeProvider, CssBaseline } from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import SideNav from "./layout/sideNav/sideNav";
import { useSelector } from "react-redux";
import ConfirmDialog from "./components/alert/ConfirmDialog";

import { LocalizationProvider } from "@mui/x-date-pickers";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import usePusherReceiver from "./app/pusherReceiver";
import { ApplicationRoutes } from "./app/routes";
import { APP_URL } from "./app/config";
import { scheduleDeliveryAlarmsGlobal, startAlarmPolling, stopAlarmPolling }
  from "./app/deliveryAlarm";
  import DeliveryBlockModal from "./app/DeliveryBlockModal";

const AppContent = ({ audioEnabled, enableOrderSound }) => {
  const location = useLocation(); // Use useLocation here
  const isChatsRoute = location.pathname === "/chats"; // Check if the current route is "/chats"

  const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
  }));

  return (
    <Box sx={{ display: "flex" }}>
      <SideNav />
      <DeliveryBlockModal />
      <Box component="main" sx={{ flexGrow: 1, p: isChatsRoute ? 0 : 3 }}>
        <DrawerHeader>
          {!audioEnabled && (
            <Button
              size="small"
              startIcon={<VolumeUpIcon />}
              onClick={enableOrderSound}
            >
              Enable order sound
            </Button>
          )}
        </DrawerHeader>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ApplicationRoutes />
        </LocalizationProvider>
      </Box>
    </Box>
  );
};

function App() {
  const { audioEnabled, enableOrderSound } = usePusherReceiver();
  useEffect(() => {
    if (Notification.permission !== "granted") {
      Notification.requestPermission();
    }
  }, []);
  const userData = useSelector((state) => state.counter?.userData);
  const themeMode = useSelector((state) => state.counter?.themeMode ?? "light");
  axios.defaults.baseURL = APP_URL;

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeMode,
          ...(themeMode === "dark"
            ? {
                primary: {
                  main: "#4f9cf9",       // brighter blue that reads well on dark bg
                  light: "#82bfff",
                  dark: "#1565c0",
                  contrastText: "#ffffff",
                },
                background: {
                  default: "#121212",
                  paper: "#1e1e1e",
                },
                text: {
                  primary: "#ffffff",
                  secondary: "#aaaaaa",
                },
              }
            : {
                background: {
                  default: "#f5f5f5",
                  paper: "#ffffff",
                },
              }),
        },
        typography: {
          fontFamily: "Montserrat, sans-serif",
        },
      }),
    [themeMode]
  );

  // Sync body class for non-MUI CSS overrides
  useEffect(() => {
    if (themeMode === "dark") {
      document.body.classList.add("dark-mode");
    } else {
      document.body.classList.remove("dark-mode");
    }
  }, [themeMode]);


useEffect(() => {
    if (!userData?.loggedIn) return;
 
    const loginType = sessionStorage.getItem("loginType") || "admin";
    const loggedInShopId = Number(sessionStorage.getItem("shop_id")) || 0;
 
    const fetchAndSchedule = async () => {
      try {
        const res = await axios.get("/listAllOrders?page=1&per_page=100");
        let orders = res?.data?.data?.data || [];
 
        // Filter by shop if shop login
        if (loginType === "shop" && loggedInShopId > 0) {
          orders = orders.filter(
            (o) =>
              o?.shop_id === loggedInShopId ||
              o?.shops?.shop_details_id === loggedInShopId ||
              o?.shop_details_id === loggedInShopId
          );
        }
 
        scheduleDeliveryAlarmsGlobal(orders);
      } catch (err) {
        console.warn("Delivery alarm fetch failed:", err);
      }
    };
 
    // Fetch immediately + re-fetch every 5 minutes for new orders
    startAlarmPolling(fetchAndSchedule);
 
    // Cleanup when user logs out or app unmounts
    return () => stopAlarmPolling();
  }, [userData?.loggedIn]); // re-runs if login state changes


  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Toaster
          position="bottom-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={themeMode === "dark" ? "dark" : "colored"}
        />
        <AlertDialog />
        <ConfirmDialog />
        {userData?.loggedIn ? (
          <AppContent audioEnabled={audioEnabled} enableOrderSound={enableOrderSound} />
        ) : (
          <ApplicationRoutes />
        )}
      </Router>
    </ThemeProvider>
  );
}

export default App;
